/**
 * Leave approval for the salon owner and manager
 * (`glamora.api.leave.requests / decide / revoke`).
 *
 * These go through `rpc` / `rpcGet`, not `useCall`: the refusals (HTTP 403 for
 * the caller's own request, 404 for another salon's, 417 for an already
 * decided request or an HRMS rule such as a missing leave balance) carry text
 * the approver must read verbatim.
 */

import { reactive } from 'vue'
import { rpc, rpcGet, verified } from './api'
import type {
  LeaveDecisionResponse,
  LeaveRequestsResponse,
  LeaveStatus,
} from './types/api'

const REQUESTS = 'glamora.api.leave.requests'
const DECIDE = 'glamora.api.leave.decide'
const REVOKE = 'glamora.api.leave.revoke'

/** Page size sent to `requests`; the server caps it at 200. */
export const LEAVE_PAGE_SIZE = 100

/** Shortest note the server accepts for a rejection or a revocation. */
export const LEAVE_NOTE_MIN = 3
/** Longest note the server accepts. */
export const LEAVE_NOTE_MAX = 500

/**
 * Pending-request count for the sidebar badge. `null` until the first answer,
 * so the badge never shows a made-up zero.
 */
export const leaveBadge = reactive<{ openCount: number | null }>({
  openCount: null,
})

/** The salon's leave requests, newest start date first. */
export function fetchLeaveRequests(args: {
  status?: LeaveStatus
  employee?: string
  limit?: number
  start?: number
}): Promise<LeaveRequestsResponse> {
  return verified<LeaveRequestsResponse>(
    rpcGet(REQUESTS, {
      status: args.status,
      employee: args.employee,
      limit: args.limit ?? LEAVE_PAGE_SIZE,
      start: args.start,
    }),
    'the leave requests',
    'leave_applications',
  )
}

/** Approve, or reject with a note (the employee reads it). */
export function decideLeave(args: {
  name: string
  decision: 'approve' | 'reject'
  note?: string
}): Promise<LeaveDecisionResponse> {
  return verified<LeaveDecisionResponse>(
    rpc(DECIDE, {
      name: args.name,
      decision: args.decision,
      note: args.note || undefined,
    }),
    'the decision',
    'leave_application',
  )
}

/** Cancel an approved request; reverses the balance and attendance. */
export function revokeLeave(args: {
  name: string
  note: string
}): Promise<LeaveDecisionResponse> {
  return verified<LeaveDecisionResponse>(
    rpc(REVOKE, {
      name: args.name,
      note: args.note,
    }),
    'the revocation',
    'leave_application',
  )
}

let badgeFetchedAt = 0
/** The badge is re-read at most this often when navigating around the app. */
const BADGE_MAX_AGE_MS = 60_000

/**
 * Refresh the sidebar badge. A failure leaves the last known count alone.
 * Unless `force`, a count younger than a minute is kept.
 */
export async function refreshLeaveBadge(force = false): Promise<void> {
  const now = Date.now()
  if (!force && now - badgeFetchedAt < BADGE_MAX_AGE_MS) return
  badgeFetchedAt = now
  try {
    // `open_count` is the pending total whatever the filters, so one row is enough.
    const res = await fetchLeaveRequests({ status: 'Open', limit: 1 })
    leaveBadge.openCount = res.open_count
  } catch {
    /* the badge is a hint; the page itself reports errors */
  }
}
